import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  BarChart2,
  ClipboardCheck,
  FileText,
  Pencil,
  Info,
  X,
} from "lucide-react";
import { Button } from "@repo/ui";
import type { Product } from "@/lib/types/inventory";

interface InventoryRowExpandedPanelProps {
  product: Product;
  onReceive: (ndc: string, itemName: string) => void;
  onDispense: (ndc: string, itemName: string) => void;
  onViewDetails: (product: Product) => void;
  onEdit: (item: Product) => void;
  onEditNotes: (item: Product) => void;
  onAudit?: (inventoryId: string) => void;
  onClose: () => void;
  /** When true, only view-only actions (Activity, View Details) are shown. */
  readOnly?: boolean;
}

export function InventoryRowExpandedPanel({
  product,
  onReceive,
  onDispense,
  onViewDetails,
  onEdit,
  onEditNotes,
  onAudit,
  onClose,
  readOnly = false,
}: InventoryRowExpandedPanelProps) {
  const navigate = useNavigate();

  const handleActivity = () => {
    navigate({ to: "/activity", search: { search: product.ndc } });
  };

  return (
    <div className="flex items-center gap-2 flex-wrap px-4 py-3 bg-muted/30 border-t border-border/30">
      {!readOnly && (
        <>
          {/* Primary actions */}
          <Button
            size="sm"
            variant="default"
            className="h-8 gap-1.5 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onReceive(product.ndc, product.item_name);
            }}
          >
            <ArrowDownToLine className="h-3.5 w-3.5" />
            Receive
          </Button>

          <Button
            size="sm"
            variant="default"
            className="h-8 gap-1.5 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onDispense(product.ndc, product.item_name);
            }}
          >
            <ArrowUpFromLine className="h-3.5 w-3.5" />
            Dispense
          </Button>

          <div className="w-px h-5 bg-border mx-1" aria-hidden />
        </>
      )}

      {/* View-only actions */}
      <Button
        size="sm"
        variant="outline"
        className="h-8 gap-1.5 text-xs"
        onClick={(e) => {
          e.stopPropagation();
          handleActivity();
        }}
      >
        <BarChart2 className="h-3.5 w-3.5" />
        Activity
      </Button>

      <Button
        size="sm"
        variant="outline"
        className="h-8 gap-1.5 text-xs"
        onClick={(e) => {
          e.stopPropagation();
          onViewDetails(product);
        }}
      >
        <Info className="h-3.5 w-3.5" />
        View Details
      </Button>

      {!readOnly && (
        <>
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(product);
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>

          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onAudit?.(product.id);
            }}
            data-testid={`inventory-adjust-${product.ndc}`}
          >
            <ClipboardCheck className="h-3.5 w-3.5" />
            Audit
          </Button>

          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onEditNotes(product);
            }}
          >
            <FileText className="h-3.5 w-3.5" />
            Notes
          </Button>
        </>
      )}

      <Button
        size="icon"
        variant="ghost"
        className="ml-auto h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
